import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { Input, UploadFiles } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import { type ReactElement, useMemo, useState } from "react";

import { CountrySelect } from "@/components/CountrySelect";
import { UsStateSelect } from "@/components/UsStateSelect";
import { composeTaxJurisdiction, parseTaxJurisdiction } from "@/data/usStates";
import {
    submitTaxIdentification,
    type TaxIdentificationPayload
} from "@/services/clientOnboarding";
import { uploadOnboardingDocument } from "@/services/clientOnboardingDocuments";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import { useReportOnboardingDraft } from "../onboardingDraftContext";
import { useLegalEntityFormState } from "../onboardingFormStateContext";
import { FormCard } from "../panel/FormCard";
import { FormPrimaryAction } from "../panel/FormPrimaryAction";
import { payloadIsDirty } from "../panel/payloadIsDirty";
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_SIZE } from "../panel/uploadConstraints";
import type { NextStepTarget } from "../types";

/**
 * Structured form for §B.2 Tax ID. Required value + jurisdiction, an
 * optional FATCA classification (free-text for v1; can become a
 * dropdown once Legal lands the allowed enum), and an optional
 * supporting document (EIN letter / jurisdictional tax-authority
 * letter / equivalent).
 */
export function TaxIdForm({
    nextStep,
    onContinue
}: {
    /** When non-null and the form is in a saved-clean state, the
     *  bottom button flips from "Saved" to "Continue to {title} →"
     *  and clicking it advances the hub to that subsection. */
    nextStep?: NextStepTarget | null;
    onContinue?: () => void;
} = {}): ReactElement {
    const context = useOnboardingApplicationContextOrNull();

    // Tax fields share the legal-entity form-state slice (the live tree
    // merged tax into the Company form; here we keep Tax ID as its own
    // section but persist through the same hub-scoped state so edits
    // survive navigation). Server hydration is handled once inside
    // OnboardingFormStateProvider, so there's no per-mount hydration
    // effect here.
    const [state, setState] = useLegalEntityFormState();
    const {
        taxIdValue,
        taxIdJurisdiction,
        fatcaClassification,
        taxDocUuid: docUuid,
        taxDocFilename: docFilename,
        lastSubmittedTax: lastSubmitted
    } = state;
    // One field on the wire (`taxIdJurisdiction`), two controls on screen:
    // the state only means anything for the US, so it rides in a `US-XX`
    // suffix rather than earning a second payload key.
    const { country: jurisdictionCountry, state: jurisdictionState } = useMemo(
        () => parseTaxJurisdiction(taxIdJurisdiction),
        [taxIdJurisdiction]
    );

    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleUpload = async (files: FileList | null): Promise<void> => {
        if (!files || files.length === 0) return;
        const file = files[0];
        if (!context) {
            setState((prev) => ({
                ...prev,
                taxDocUuid: `mock-${Date.now()}`,
                taxDocFilename: file.name
            }));
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const doc = await uploadOnboardingDocument({
                applicationUuid: context.application.uuid,
                purpose: "tax_id_document",
                file
            });
            setState((prev) => ({
                ...prev,
                taxDocUuid: doc.uuid,
                taxDocFilename: doc.filename
            }));
        } catch (err) {
            setError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Document upload failed. Choose the file again and retry."
                )
            );
        } finally {
            setUploading(false);
        }
    };

    const submittable =
        taxIdValue.trim().length > 0 && taxIdJurisdiction.trim().length > 0;

    const currentPayload = useMemo<TaxIdentificationPayload | null>(() => {
        if (!submittable) return null;
        return {
            taxIdValue: taxIdValue.trim(),
            taxIdJurisdiction: taxIdJurisdiction.trim(),
            taxIdDocumentUuid: docUuid,
            fatcaClassification: fatcaClassification.trim() || null
        };
    }, [
        submittable,
        taxIdValue,
        taxIdJurisdiction,
        docUuid,
        fatcaClassification
    ]);

    const isDirty =
        lastSubmitted !== null && payloadIsDirty(currentPayload, lastSubmitted);
    const hasPartialInput =
        taxIdValue.trim().length > 0 ||
        taxIdJurisdiction.trim().length > 0 ||
        fatcaClassification.trim().length > 0 ||
        docUuid !== null;
    const hasDraft = isDirty || (lastSubmitted === null && hasPartialInput);
    useReportOnboardingDraft("tax-identification", hasDraft);

    const handleSave = async (): Promise<void> => {
        if (!currentPayload) return;
        if (!context) {
            setState((prev) => ({ ...prev, lastSubmittedTax: currentPayload }));
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await submitTaxIdentification(
                context.application.uuid,
                currentPayload
            );
            context.applyStepUpdate(result);
            setState((prev) => ({ ...prev, lastSubmittedTax: currentPayload }));
        } catch (err) {
            setError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Tax ID details were not saved. Complete the required fields and try again."
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title="Tax ID"
                className="pb-4 border-b border-line-base"
            />

            <FormCard
                title="Tax ID Number"
                hint="EIN for US entities, or the jurisdictional equivalent (TIN, CRN, etc.)."
            >
                <Input
                    aria-label="Tax ID Number"
                    value={taxIdValue}
                    onChange={(e) =>
                        setState((prev) => ({
                            ...prev,
                            taxIdValue: e.target.value
                        }))
                    }
                    placeholder="e.g. 12-3456789"
                />
            </FormCard>

            <FormCard
                title="Issuing Jurisdiction"
                hint="The country that issued the tax ID."
            >
                <CountrySelect
                    value={jurisdictionCountry}
                    onChange={(code) =>
                        setState((prev) => ({
                            ...prev,
                            // Switching away from the US drops any state that
                            // was chosen, so `US-DE` cannot survive as `GB-DE`.
                            taxIdJurisdiction: composeTaxJurisdiction(
                                code,
                                code === "US" ? jurisdictionState : null
                            )
                        }))
                    }
                />
            </FormCard>

            {jurisdictionCountry === "US" && (
                <FormCard title="Issuing State">
                    <UsStateSelect
                        value={jurisdictionState}
                        onChange={(code) =>
                            setState((prev) => ({
                                ...prev,
                                taxIdJurisdiction: composeTaxJurisdiction(
                                    "US",
                                    code
                                )
                            }))
                        }
                    />
                </FormCard>
            )}

            <FormCard
                title="FATCA Classification (Optional)"
                hint="If you've completed a FATCA classification (e.g. as part of a banking relationship), enter it here."
            >
                <Input
                    aria-label="FATCA Classification (Optional)"
                    value={fatcaClassification}
                    onChange={(e) =>
                        setState((prev) => ({
                            ...prev,
                            fatcaClassification: e.target.value
                        }))
                    }
                    placeholder="e.g. Active NFFE"
                />
            </FormCard>

            <FormCard
                title="Tax ID Document (Optional)"
                hint="Upload the official letter from the tax authority that assigned the ID."
            >
                <UploadFiles
                    className="max-w-sm mx-auto mt-6"
                    size="sm"
                    accept={DOCUMENT_ACCEPT}
                    maxSize={DOCUMENT_MAX_SIZE}
                    state={
                        uploading ? "uploading" : docUuid ? "success" : "empty"
                    }
                    filename={docFilename ?? undefined}
                    onSelect={(files) => void handleUpload(files)}
                />
            </FormCard>

            <FormPrimaryAction
                submittable={submittable}
                submitting={submitting}
                isSavedClean={lastSubmitted !== null && !isDirty}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
                onSave={() => void handleSave()}
                error={error}
            />
        </motion.div>
    );
}
